import { PermissionManager } from "@utils/permissionManager";
import { getCjmInteractionById, linkInteraction, unlinkInteraction, updateInteraction } from "features/journey-map/reducer/CJMActions";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getInteractionSelector } from "redux/memoizedSelectors";
import DataLoader from "shared/components/data-loader";
import Header from "shared/cx-components/header";
import { Descriptions, HeaderTouchpoints, PropertyManager, RelationManager } from "./components/index";
import { Body, OffcanvasLayout } from "./personaInteractions_style";

function EditInteraction({ isOpen, onClose, ...props }) {
	const { stageId } = props;
	const { cjmIdStr, personaIdStr } = useLocation().search.getAllMapsParams();
	const { t } = useTranslation();

	const isReadOnly = useSelector((state) => state.auth.userInfo.UserType)?.isReadOnly();
	const userAccess = useSelector((state) => state?.maps?.currentUser?.roleName);
	const current_user = useSelector((state) => state.maps.currentUser);

	const { editAllowed } = PermissionManager({ userAccess });
	const isEditable = !isReadOnly && editAllowed;
	const mode = isEditable ? "edit" : "viewer";
	const subscriptionPlan = useSelector((state) => state.auth?.userInfo?.UserSubscriptionPlanName);

	const isEnterprise = subscriptionPlan?.toLowerCase() === "enterprise";
	const isSharedEnterprise = subscriptionPlan?.toLowerCase() === "sharedenterprise";
	const [currentItemId, updateCurrentItem] = React.useState(props.interaction?.id);
	const stagesInteractionsIds = useSelector(getInteractionSelector(personaIdStr, stageId)).map(interact => interact.id);
	const interaction = useSelector(getInteractionSelector(personaIdStr, stageId, currentItemId));
	const { channelId } = interaction || {};
	const updateFunction = (props) => {
		updateInteraction({
			...props,
			interaction: props.item,
			cjmIdStr,
			personaIdStr,
			modifiedBy: current_user?.fullName,
			stageId
		});
	};


	useEffect(() => {
		if (!cjmIdStr || !personaIdStr) {
			// means we got redirected to another page
			return () => {
				onClose();
			};
		}
	}, [cjmIdStr, personaIdStr]);
	const testArray = {
		displayed: ["persona", "sentiment", 'group'],
		extra: ["systems", "departments", "linkedTouchpoints"],
	};
	const defaultRows = interaction?.jsonProperty
		? analyzeJSONproperty(interaction?.jsonProperty, (isEnterprise || isSharedEnterprise))
		: {
			displayed: ["persona", "sentiment", ...(isEnterprise || isSharedEnterprise ? ["mainRoute", "alternativeRoute"] : [])],
			extra: ["systems", "departments", "linkedTouchpoints"],
		};


	return (
		<OffcanvasLayout show={isOpen} onHide={onClose} scroll={true} backdrop={true} placement={"end"} container={document.getElementById("modal-root")}>
			<DataLoader key={currentItemId} asyncFns={[{ asyncFn: getCjmInteractionById, parameter: { cjmId: cjmIdStr, stageId, personaId: personaIdStr, personaInteractionId: currentItemId } }]}>
				<div className="p-3 px-4">
					<Header onClose={onClose} showCopyUrl={false} actions={[]} navigationList={stagesInteractionsIds} currentItemId={currentItemId} setCurrentItem={updateCurrentItem} disabled={false} />
				</div>

				<Body key={interaction?.id}>
					{channelId && <HeaderTouchpoints channelId={channelId} object={interaction} />}
					<Descriptions object={interaction} update={updateFunction} attribute="actions" placeholder={t("ACTION_TOUCHPOINT_PLACEHOLDER")} mode={mode} />
					<PropertyManager
						item={interaction}
						updateFunction={updateFunction}
						mode={mode}
						recordType="cem_customerexperience"
						defaultRows={defaultRows}
						linkFunction={linkInteraction}
						unlinkFunction={unlinkInteraction}
						stageId={stageId}
					/>
					<RelationManager stageId={stageId} item={interaction} mode={mode} onClose={onClose} defaultKey="ASPX_33" dataLoading={cjmIdStr && personaIdStr ? false : true} />
				</Body>
			</DataLoader>
		</OffcanvasLayout>
	);
}

const analyzeJSONproperty = (jsonProperty, allowMain) => {
	try {
		const parsedJson = JSON.parse(jsonProperty);

		if (!Array.isArray(parsedJson.displayed)) {
			parsedJson.displayed = [];
		}

		if (allowMain) {
			const hasMainRoute = parsedJson.displayed.includes("mainRoute");
			const hasAlternativeRoute = parsedJson.displayed.includes("alternativeRoute");

			// If at least one of them is missing, ensure both are added
			if (!hasMainRoute || !hasAlternativeRoute) {
				const sentimentIndex = parsedJson.displayed.indexOf("sentiment");

				if (sentimentIndex !== -1) {
					// Insert missing routes after "sentiment"
					const newItems = [];
					if (!hasMainRoute) newItems.push("mainRoute");
					if (!hasAlternativeRoute) newItems.push("alternativeRoute");

					parsedJson.displayed = [
						...parsedJson.displayed.slice(0, sentimentIndex + 1),
						...newItems,
						...parsedJson.displayed.slice(sentimentIndex + 1),
					];
				} else {
					// Append missing routes at the end
					if (!hasMainRoute) parsedJson.displayed.push("mainRoute");
					if (!hasAlternativeRoute) parsedJson.displayed.push("alternativeRoute");
				}
			}
		}
		return parsedJson;
	} catch (error) {
		console.error("Invalid JSON:", error);
		return null; // or handle error as needed
	}
};


EditInteraction.displayName = "EditInteraction";

EditInteraction.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	interaction: PropTypes.object,
	stageId: PropTypes.string,
};

export default React.memo(EditInteraction);
